import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { map, Observable, startWith } from 'rxjs';
import { take } from 'rxjs/operators';
import { MemberUser } from '../../../../models/users/dto/member-user';
import { LoadingOptions } from '../../../../models/shared/loading-options';
import { ChangeNameRequest } from '../../../../models/users/requests/submissionRequests/change-name-request';
import { MediaUtils } from '../../../../utils/media-utils';
import { NameChangeSubmissionFormViewModel } from './name-change-submission-form-view-model';
import { maxFileSizeValidator } from '../../../../ui/form-components/file-uploader/file-upload-field/max-file-size-validator';

type NameChangeForm = FormGroup<{
  firstName: FormControl<string>;
  middleName: FormControl<string>;
  lastName: FormControl<string>;
  preferredName: FormControl<string>;
  files: FormControl<File[]>;
}>;

@Component({
  selector: 'app-name-change-submission-form',
  templateUrl: './name-change-submission-form.component.html',
  styleUrls: ['./name-change-submission-form.component.scss'],
  providers: [NameChangeSubmissionFormViewModel],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NameChangeSubmissionFormComponent implements OnChanges {
  @Input() user!: MemberUser;
  @Input() acceptedFileTypes: string[] = ['image/png', 'image/jpeg', 'application/pdf'];
  @Input() allowMultipleFiles: boolean = false;
  @Input() maxFileSizeInMb: number = 3;

  @Output() loadingOpts = new EventEmitter<LoadingOptions>();

  private viewModel = inject(NameChangeSubmissionFormViewModel);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  public formGroup: NameChangeForm = this.buildForm();
  public formSubmitted = false;

  public readonly customErrorMessages = {
    files: {
      required: $localize`A file upload is required`,
      maxFileSize: $localize`File size exceeds the maximum limit of ${this.maxFileSizeInMb}MB`
    }
  };

  public readonly infoChanged$: Observable<boolean> = this.formGroup.valueChanges.pipe(
    startWith(this.formGroup.value),
    map(() => this.hasInfoChanged())
  );

  public readonly uploadTitle$ = this.infoChanged$.pipe(map(changed => (changed ? $localize`Upload a file` : '')));

  public readonly uploadBody$ = this.infoChanged$.pipe(
    map(changed => {
      return changed
        ? $localize`or drag and drop .png or .jpeg up to 3MB`
        : $localize`Images cannot be uploaded at this time.`;
    })
  );

  constructor() {
    this.viewModel.loadingOpts$.pipe(takeUntilDestroyed()).subscribe(opts => this.loadingOpts.emit(opts));

    this.infoChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(changed => {
      const filesControl = this.formGroup.controls.files;
      if (changed) {
        filesControl.setValidators([Validators.required, maxFileSizeValidator(this.maxFileSizeInMb)]);
        filesControl.enable({ emitEvent: false });
      } else {
        filesControl.clearValidators();
        filesControl.setValue([], { emitEvent: false });
        filesControl.disable({ emitEvent: false });
      }
      filesControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.user && this.user) {
      this.resetFormFromUser();
      this.cdr.markForCheck();
    }
  }

  public submitForm(): void {
    this.formSubmitted = true;
    this.formGroup.markAllAsTouched();
    this.cdr.markForCheck();
    if (this.formGroup.invalid) return;

    const request = this.buildRequest();
    this.infoChanged$.pipe(take(1)).subscribe(changed => {
      const files = this.formGroup.controls.files.value;
      if (changed && files.length) {
        Promise.all(files.map(file => MediaUtils.fileToUserFile(file))).then(userFiles => {
          this.viewModel.submitRequest(request, userFiles);
        });
      } else {
        this.viewModel.submitRequest(request);
      }
    });
  }

  private buildForm(): NameChangeForm {
    return new FormGroup({
      firstName: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      middleName: new FormControl<string>('', { nonNullable: true }),
      lastName: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      preferredName: new FormControl<string>('', { nonNullable: true }),
      files: new FormControl<File[]>({ value: [], disabled: true }, { nonNullable: true })
    });
  }

  private resetFormFromUser(): void {
    this.formGroup.reset(
      {
        firstName: this.user.firstName ?? '',
        middleName: this.user.middleName ?? '',
        lastName: this.user.lastName ?? '',
        preferredName: this.user.preferredName ?? '',
        files: []
      },
      { emitEvent: false }
    );
    this.formGroup.controls.files.disable({ emitEvent: false });
    this.formSubmitted = false;
    this.formGroup.updateValueAndValidity();
  }

  private hasInfoChanged(): boolean {
    if (!this.user) return false;
    const { firstName, middleName, lastName } = this.formGroup.getRawValue();
    return (
      firstName !== (this.user.firstName ?? '') ||
      middleName !== (this.user.middleName ?? '') ||
      lastName !== (this.user.lastName ?? '')
    );
  }

  private buildRequest(): ChangeNameRequest {
    const value = this.formGroup.getRawValue();
    const request = new ChangeNameRequest(this.user);
    request.firstName = value.firstName;
    request.middleName = value.middleName;
    request.lastName = value.lastName;
    request.preferredName = value.preferredName;
    return request;
  }
}
